import React, { useEffect, useRef, useState, useCallback } from "react";
import { http, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Loader2, BadgeCheck, Camera, Eye, EyeOff, MapPin, Briefcase,
  Music, HandHelping, Shield, ChevronDown, ChevronUp, Star, Users,
  Church, Search, Plus, CheckCircle2, AlertCircle, Building2, User,
} from "lucide-react";
import { toast } from "sonner";
import VerifiedBadge from "../components/VerifiedBadge";

const PRIVACY_OPTS = [
  { v: "public", l: "Public" },
  { v: "members", l: "Members only" },
  { v: "private", l: "Hidden" },
];

function PrivacySelect({ field, value, onChange }) {
  return (
    <select
      className="text-xs border border-[var(--border-default)] rounded px-2 py-1 bg-[var(--bg-paper)] text-[var(--text-secondary)]"
      value={value || "members"}
      onChange={(e) => onChange(field, e.target.value)}
      data-testid={`privacy-${field}`}
    >
      {PRIVACY_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}

function AvatarUploader({ currentAvatar, name, onUploaded }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(currentAvatar || "");
  const [uploading, setUploading] = useState(false);

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result);
    reader.readAsDataURL(file);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated");
      if (onUploaded) onUploaded(data.avatar);
    } catch (ex) { toast.error(formatErr(ex)); } finally { setUploading(false); }
  };

  const initials = (name || "U").slice(0, 1).toUpperCase();
  return (
    <div className="relative group w-24 h-24 shrink-0" data-testid="avatar-uploader">
      <div className="w-24 h-24 rounded-2xl overflow-hidden bg-[var(--brand-primary)] text-white flex items-center justify-center text-3xl font-display shadow-md">
        {preview ? <img src={preview} alt="avatar" className="w-full h-full object-cover" /> : initials}
        {uploading && <div className="absolute inset-0 bg-black/40 grid place-items-center"><Loader2 size={20} className="animate-spin text-white" /></div>}
      </div>
      <button onClick={pick} className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-[var(--brand-accent)] text-white grid place-items-center shadow hover:scale-110 transition-transform" data-testid="avatar-pick">
        <Camera size={14} />
      </button>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
    </div>
  );
}

function ParishMembershipSection({ memberships, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("search"); // search | confirm | create | dup | done
  const [pq, setPq] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [candidate, setCandidate] = useState(null);
  const [dups, setDups] = useState([]);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [err, setErr] = useState("");
  const [pform, setPform] = useState({
    name: "", city: "", country: "", state: "", address: "", shepherd_name: "", phone: "",
  });

  const pf = (k) => (e) => setPform((f) => ({ ...f, [k]: e.target.value }));

  // Debounced live search
  useEffect(() => {
    if (phase !== "search" || !pq.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data: res } = await http.get("/parishes", { params: { q: pq } });
        setResults(res);
      } catch { setResults([]); }
      setSearching(false);
    }, 380);
    return () => clearTimeout(t);
  }, [pq, phase]);

  const confirmExisting = async (parish) => {
    setJoining(true); setErr("");
    try {
      await http.post(`/parishes/${parish.id}/join`);
      toast.success(`Joined ${parish.name}`);
      setPhase("done");
      setCandidate(parish);
      onRefresh();
    } catch (e) { setErr(formatErr(e)); }
    setJoining(false);
  };

  const handleCreate = async (forceCreate = false) => {
    if (!pform.name.trim() || !pform.city.trim() || !pform.country.trim()) {
      setErr("Parish name, city and country are required."); return;
    }
    setCreating(true); setErr("");
    try {
      const { data: res } = await http.post("/parishes/lookup-or-create", {
        ...pform,
        force_create: forceCreate,
      });
      if (!forceCreate && res.matches?.length > 0) {
        setDups(res.matches);
        setPhase("dup");
      } else {
        const parish = res.parish;
        // Auto-join the newly created parish
        await http.post(`/parishes/${parish.id}/join`);
        toast.success(`Added and joined ${parish.name}`);
        setCandidate(parish);
        setPhase("done");
        onRefresh();
      }
    } catch (e) { setErr(formatErr(e)); }
    setCreating(false);
  };

  const reset = () => {
    setOpen(false);
    setPhase("search");
    setPq("");
    setResults([]);
    setCandidate(null);
    setDups([]);
    setErr("");
    setPform({ name: "", city: "", country: "", state: "", address: "", shepherd_name: "", phone: "" });
  };

  const primary = memberships[0];

  return (
    <div className="card-surface p-5 space-y-4" data-testid="parish-membership-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Church size={16} className="text-[var(--brand-accent)]" />
          <div className="font-semibold text-sm text-[var(--brand-primary)]">Parish</div>
        </div>
        <button
          onClick={() => { setOpen((s) => !s); if (open) reset(); }}
          className="text-xs text-[var(--brand-accent)] font-medium hover:underline"
        >
          {open ? "Close" : memberships.length ? "Change parish" : "Add parish"}
        </button>
      </div>

      {!open && primary && (
        <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3.5">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
            <Church size={18} className="text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-[var(--brand-primary)]">{primary.parish?.name}</div>
            <div className="text-xs text-[var(--text-tertiary)] mt-0.5 flex items-center gap-1">
              <MapPin size={10} /> {[primary.parish?.city, primary.parish?.state, primary.parish?.country].filter(Boolean).join(", ")}
            </div>
            {primary.parish?.shepherd_name && (
              <div className="text-xs text-[var(--text-tertiary)] mt-0.5 flex items-center gap-1">
                <User size={10} /> {primary.parish?.shepherd_name}
              </div>
            )}
          </div>
        </div>
      )}
      {!open && !memberships.length && (
        <div className="text-sm text-[var(--text-tertiary)]">
          You haven't joined a parish yet. Click "Add parish" to find or create yours.
        </div>
      )}

      {open && (
        <div className="space-y-3">
          {/* ── DONE ── */}
          {phase === "done" && candidate && (
            <div>
              <div className="rounded-2xl p-4 border-2 border-emerald-300 bg-emerald-50/40">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                    <Church size={19} className="text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[var(--brand-primary)] text-sm">{candidate.name}</div>
                    {(candidate.city || candidate.country) && (
                      <div className="flex items-center gap-1 text-xs text-[var(--text-tertiary)] mt-0.5">
                        <MapPin size={10} className="shrink-0" />
                        {[candidate.city, candidate.state, candidate.country].filter(Boolean).join(", ")}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <button onClick={reset} className="mt-3 text-xs text-[var(--brand-accent)] hover:underline">
                Done — close panel
              </button>
            </div>
          )}

          {/* ── CONFIRM ── */}
          {phase === "confirm" && candidate && (
            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-3">Use this parish?</p>
              <div className="rounded-2xl border-2 border-[var(--brand-primary)]/15 bg-[var(--bg-subtle)] p-4 mb-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-[var(--brand-primary)]/10 flex items-center justify-center shrink-0">
                    <Church size={20} className="text-[var(--brand-primary)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-[var(--brand-primary)]">{candidate.name}</div>
                    {(candidate.city || candidate.country) && (
                      <div className="flex items-center gap-1 text-xs text-[var(--text-tertiary)] mt-0.5">
                        <MapPin size={10} /> {[candidate.city, candidate.state, candidate.country].filter(Boolean).join(", ")}
                      </div>
                    )}
                    {candidate.shepherd_name && (
                      <div className="flex items-center gap-1 text-xs text-[var(--text-tertiary)] mt-0.5">
                        <User size={10} /> {candidate.shepherd_name}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => confirmExisting(candidate)} disabled={joining} className="btn-primary flex items-center justify-center gap-2 py-2.5 text-sm">
                  {joining ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  Yes, use this parish
                </button>
                <button
                  onClick={() => { setCandidate(null); setPhase("search"); }}
                  className="px-4 py-2.5 rounded-xl border border-[var(--border-default)] text-sm text-[var(--text-secondary)] hover:border-[var(--brand-primary)] transition-colors"
                >
                  No, keep searching
                </button>
              </div>
              {err && <div className="mt-2 text-xs text-red-600">{err}</div>}
            </div>
          )}

          {/* ── DUP ── */}
          {phase === "dup" && (
            <div>
              <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-amber-50 border border-amber-200 mb-3">
                <AlertCircle size={15} className="text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-amber-800">Similar parishes found</div>
                  <div className="text-xs text-amber-700 mt-0.5">Could any of these be your parish? Selecting an existing one avoids duplicates.</div>
                </div>
              </div>
              <div className="space-y-2 mb-3 max-h-52 overflow-y-auto pr-1">
                {dups.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => confirmExisting(p)}
                    className="w-full text-left p-3 rounded-xl border border-[var(--border-default)] hover:border-emerald-400 hover:bg-emerald-50/40 transition-all group"
                  >
                    <div className="flex items-start gap-3">
                      <Church size={14} className="text-[var(--brand-primary)] shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-[var(--brand-primary)]">{p.name}</div>
                        <div className="text-xs text-[var(--text-tertiary)] mt-0.5">
                          {[p.city, p.state, p.country].filter(Boolean).join(", ")}
                          {p.shepherd_name && ` · Shep. ${p.shepherd_name}`}
                        </div>
                      </div>
                      <span className="text-[10px] text-emerald-600 font-semibold opacity-0 group-hover:opacity-100 shrink-0">Use this →</span>
                    </div>
                  </button>
                ))}
              </div>
              <div className="space-y-2">
                <button
                  onClick={() => handleCreate(true)}
                  disabled={creating}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[var(--brand-accent)]/40 bg-amber-50/50 hover:bg-amber-50 text-sm text-[var(--brand-primary)] font-medium transition-colors"
                >
                  {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  None of these — add my parish anyway
                </button>
                <button onClick={() => setPhase("create")} className="w-full text-center text-xs text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] py-1">
                  ← Edit parish details
                </button>
              </div>
              {err && <div className="mt-2 text-xs text-red-600">{err}</div>}
            </div>
          )}

          {/* ── CREATE ── */}
          {phase === "create" && (
            <div className="space-y-2.5">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[var(--brand-accent)]/10 flex items-center justify-center shrink-0">
                  <Building2 size={15} className="text-[var(--brand-accent)]" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[var(--brand-primary)]">Add your parish to the directory</div>
                  <div className="text-xs text-[var(--text-tertiary)]">Help grow the worldwide CCC map.</div>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-tertiary)] block mb-1">Parish Name <span className="text-red-500">*</span></label>
                <input className="input-clean" placeholder="e.g., CCC Morning Star Parish" value={pform.name} onChange={pf("name")} autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-tertiary)] block mb-1">City <span className="text-red-500">*</span></label>
                  <input className="input-clean" placeholder="e.g., Lagos" value={pform.city} onChange={pf("city")} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-tertiary)] block mb-1">Country <span className="text-red-500">*</span></label>
                  <input className="input-clean" placeholder="e.g., Nigeria" value={pform.country} onChange={pf("country")} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-tertiary)] block mb-1">State / Region</label>
                  <input className="input-clean" placeholder="Optional" value={pform.state} onChange={pf("state")} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-tertiary)] block mb-1">Contact Number</label>
                  <input className="input-clean" placeholder="+234 801…" value={pform.phone} onChange={pf("phone")} />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-tertiary)] block mb-1">Shepherd / Pastor in Charge</label>
                <input className="input-clean" placeholder="e.g., Snr. Apostle E. Adeyemi" value={pform.shepherd_name} onChange={pf("shepherd_name")} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-tertiary)] block mb-1">Full Address</label>
                <textarea className="input-clean min-h-[60px] resize-none text-sm" placeholder="Street, area, city…" value={pform.address} onChange={pf("address")} />
              </div>
              {err && <div className="text-xs text-red-600">{err}</div>}
              <div className="flex gap-2 pt-1">
                <button onClick={() => setPhase("search")} className="px-4 py-2.5 rounded-xl border border-[var(--border-default)] text-sm text-[var(--text-secondary)] hover:border-[var(--brand-primary)] transition-colors">
                  ← Back
                </button>
                <button
                  onClick={() => handleCreate(false)}
                  disabled={!pform.name.trim() || !pform.city.trim() || !pform.country.trim() || creating}
                  className="flex-1 btn-primary flex items-center justify-center gap-2 text-sm"
                >
                  {creating ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                  Add my parish
                </button>
              </div>
            </div>
          )}

          {/* ── SEARCH ── */}
          {phase === "search" && (
            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-3">Search the worldwide CCC directory for your parish.</p>
              <div className="relative mb-3">
                <input
                  className="input-clean pr-10"
                  placeholder="e.g., CCC Heavenly Light Parish, Lagos"
                  value={pq}
                  onChange={(e) => setPq(e.target.value)}
                  autoFocus
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none">
                  {searching ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
                </div>
              </div>
              {pq.trim() && results.length > 0 && (
                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-0.5 mb-3">
                  {results.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { setCandidate(p); setPhase("confirm"); }}
                      className="w-full text-left px-4 py-3 rounded-xl border border-[var(--border-default)] hover:border-[var(--brand-accent)] hover:bg-[var(--bg-subtle)] transition-all group"
                    >
                      <div className="flex items-start gap-3">
                        <Church size={14} className="text-[var(--brand-primary)] shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-[var(--brand-primary)] truncate">{p.name}</div>
                          <div className="text-xs text-[var(--text-tertiary)] mt-0.5 truncate">
                            {[p.city, p.state, p.country].filter(Boolean).join(", ")}
                            {p.shepherd_name && ` · Shep. ${p.shepherd_name}`}
                          </div>
                        </div>
                        <span className="text-[10px] text-[var(--brand-accent)] font-semibold opacity-0 group-hover:opacity-100 shrink-0">Tap →</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {pq.trim() && !searching && (
                <button
                  onClick={() => setPhase("create")}
                  className="w-full flex items-start gap-3 p-3.5 rounded-xl border-2 border-dashed border-[var(--brand-accent)]/35 bg-amber-50/20 hover:bg-amber-50/60 hover:border-[var(--brand-accent)]/60 transition-all text-left"
                >
                  <div className="w-8 h-8 rounded-lg bg-[var(--brand-accent)]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Plus size={14} className="text-[var(--brand-accent)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[var(--brand-primary)] truncate">
                      {results.length > 0 ? `My parish isn't listed — add it` : `Add "${pq}" to the directory`}
                    </div>
                    <div className="text-xs text-[var(--text-tertiary)] mt-0.5">Help grow the worldwide CCC parish map</div>
                  </div>
                </button>
              )}
              {!pq.trim() && (
                <div className="flex flex-col items-center py-6 text-center">
                  <Church size={32} className="text-[var(--brand-primary)]/15 mb-2" />
                  <div className="text-sm text-[var(--text-tertiary)]">Start typing to find your parish</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Profile() {
  const { user, refresh } = useAuth();
  const [ranks, setRanks] = useState([]);
  const [choirStatus, setChoirStatus] = useState([]);
  const [serviceTeams, setServiceTeams] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loadingMemberships, setLoadingMemberships] = useState(false);
  const [form, setForm] = useState({
    name: "", ccc_rank: "", country: "", city: "",
    profile_summary: "", career_summary: "",
    interested_in_choir: false, directory_visible: true,
    privacy: {},
  });
  const [busy, setBusy] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const loadMemberships = useCallback(async () => {
    setLoadingMemberships(true);
    try {
      const { data } = await http.get("/me/memberships");
      setMemberships(data || []);
    } catch { setMemberships([]); }
    setLoadingMemberships(false);
  }, []);

  useEffect(() => {
    http.get("/settings/ccc_ranks").then((r) => setRanks(r.data)).catch(() => {});
    http.get("/me/choir-status").then((r) => setChoirStatus(r.data)).catch(() => {});
    http.get("/me/service-teams").then((r) => setServiceTeams(r.data)).catch(() => {});
    loadMemberships();
  }, [loadMemberships]);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        ccc_rank: user.ccc_rank || "",
        country: user.country || "",
        city: user.city || "",
        profile_summary: user.profile_summary || "",
        career_summary: user.career_summary || "",
        interested_in_choir: !!user.interested_in_choir,
        directory_visible: user.directory_visible !== false,
        privacy: user.privacy || {},
      });
    }
  }, [user]);

  const setPrivacy = (field, value) => setForm((f) => ({ ...f, privacy: { ...f.privacy, [field]: value } }));

  const save = async () => {
    setBusy(true);
    try {
      await http.patch("/me/profile", form);
      toast.success("Profile saved");
      refresh();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const completionFields = ["name", "ccc_rank", "country", "city", "profile_summary"];
  const filled = completionFields.filter((f) => !!form[f]).length + (memberships.length > 0 ? 1 : 0);
  const pct = Math.round((filled / (completionFields.length + 1)) * 100);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-[var(--brand-accent)]">Identity</div>
        <h1 className="font-display text-3xl sm:text-4xl text-[var(--brand-primary)]">My Profile</h1>
        <p className="text-xs text-[var(--text-tertiary)] mt-1">{user?.email} · {user?.role}</p>
      </div>

      {/* Completion banner */}
      {pct < 100 && (
        <div className="card-surface p-4 flex items-center gap-4" data-testid="profile-completion">
          <div className="flex-1">
            <div className="text-sm font-medium text-[var(--brand-primary)]">Profile {pct}% complete</div>
            <div className="h-1.5 bg-[var(--bg-subtle)] rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-[var(--brand-accent)] rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <div className="text-xs text-[var(--text-tertiary)] shrink-0">Add a photo, bio, and city to complete your profile.</div>
        </div>
      )}

      {/* Identity row */}
      <div className="card-surface p-6 flex items-start gap-5 flex-wrap">
        <AvatarUploader currentAvatar={user?.avatar} name={user?.name} onUploaded={() => refresh()} />
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <div className="font-display text-2xl text-[var(--brand-primary)]">{form.name || user?.name}</div>
            {user?.verified && <VerifiedBadge size="sm" reason={user?.verified_reason} />}
          </div>
          {form.ccc_rank && <div className="text-sm text-[var(--brand-accent)] font-medium">{form.ccc_rank}</div>}
          {(form.country || form.city) && (
            <div className="text-xs text-[var(--text-tertiary)] flex items-center gap-1">
              <MapPin size={11} /> {[form.city, form.country].filter(Boolean).join(", ")}
            </div>
          )}
          {/* Parish */}
          {(() => {
            const p = memberships[0]?.parish || user?.primary_parish;
            if (!p) return null;
            return (
              <div className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] pt-0.5">
                <Church size={11} className="text-[var(--brand-accent)]" />
                <span className="font-medium">{p.name}</span>
                <span className="text-[var(--text-tertiary)]">
                  {[p.city, p.country].filter(Boolean).join(", ")}
                </span>
              </div>
            );
          })()}
          {/* Badges */}
          {user?.badges?.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-1">
              {user.badges.map((b) => (
                <span key={b} className="inline-flex items-center gap-1 text-[10px] px-2.5 py-0.5 rounded-full bg-[var(--bg-subtle)] text-[var(--brand-primary)] border border-[var(--border-default)]" data-testid={`badge-${b}`}>
                  <BadgeCheck size={10} className="text-[var(--brand-accent)]" /> {b}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Choir + Service chips */}
      {(choirStatus.length > 0 || serviceTeams.length > 0) && (
        <div className="flex flex-wrap gap-2" data-testid="profile-service-chips">
          {choirStatus.filter((c) => c.status === "verified").map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-[var(--brand-accent)]/10 text-[var(--brand-accent)] border border-[var(--brand-accent)]/30">
              <Music size={11} /> {c.role === "director" ? "Choir Director" : "Choir"} · {c.voice_part}
            </span>
          ))}
          {serviceTeams.filter((s) => s.status === "approved").map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              <HandHelping size={11} /> {s.service_type} · {s.parish?.name}
            </span>
          ))}
        </div>
      )}

      {/* Parish membership */}
      <ParishMembershipSection memberships={memberships} onRefresh={loadMemberships} />

      {/* Edit form */}
      <div className="card-surface p-6 grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <div className="text-xs uppercase tracking-widest font-semibold text-[var(--text-tertiary)] mb-3">Personal Information</div>
        </div>
        <label className="text-sm">
          Full name
          <input className="input-clean mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
        </label>
        <label className="text-sm">
          CCC Rank / Title
          <select className="input-clean mt-1" value={form.ccc_rank} onChange={(e) => setForm({ ...form, ccc_rank: e.target.value })} data-testid="profile-rank">
            <option value="">— Select rank —</option>
            {ranks.map((r) => <option key={r.id} value={r.label}>{r.label}</option>)}
          </select>
        </label>
        <label className="text-sm">
          Country
          <input className="input-clean mt-1" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="profile-country" />
        </label>
        <label className="text-sm">
          City
          <div className="flex gap-1.5 mt-1">
            <input className="input-clean flex-1" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="profile-city" />
            <PrivacySelect field="city" value={form.privacy?.city} onChange={setPrivacy} />
          </div>
        </label>
        <label className="text-sm sm:col-span-2">
          Bio / Profile summary
          <textarea className="input-clean mt-1 min-h-[90px] resize-none" value={form.profile_summary} onChange={(e) => setForm({ ...form, profile_summary: e.target.value })} placeholder="Share a little about yourself with fellow Celestials…" data-testid="profile-bio" />
        </label>
        <label className="text-sm sm:col-span-2">
          Career / Professional summary
          <div className="flex gap-1.5 mt-1 items-start">
            <textarea className="input-clean flex-1 min-h-[70px] resize-none" value={form.career_summary} onChange={(e) => setForm({ ...form, career_summary: e.target.value })} placeholder="Your professional background or skills (optional — useful for Jobs & Careers networking)…" data-testid="profile-career" />
            <PrivacySelect field="career_summary" value={form.privacy?.career_summary} onChange={setPrivacy} />
          </div>
        </label>
        <label className="text-sm flex items-center gap-2 sm:col-span-2">
          <input type="checkbox" checked={form.interested_in_choir} onChange={(e) => setForm({ ...form, interested_in_choir: e.target.checked })} />
          Interested in joining / staying in choir ministry
        </label>
        <label className="text-sm flex items-center gap-2 sm:col-span-2">
          <input type="checkbox" checked={form.directory_visible} onChange={(e) => setForm({ ...form, directory_visible: e.target.checked })} data-testid="profile-visible" />
          Appear in Meet People directory
        </label>

        {/* Privacy section */}
        <div className="sm:col-span-2 border-t border-[var(--border-default)] pt-3">
          <button onClick={() => setPrivacyOpen((s) => !s)} className="text-xs text-[var(--text-tertiary)] flex items-center gap-1 hover:text-[var(--brand-primary)]">
            {privacyOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Privacy settings
          </button>
          {privacyOpen && (
            <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm" data-testid="privacy-panel">
              {[["city", "City visibility"], ["career_summary", "Career summary visibility"]].map(([f, l]) => (
                <div key={f} className="flex items-center justify-between gap-2">
                  <span className="text-[var(--text-secondary)]">{l}</span>
                  <PrivacySelect field={f} value={form.privacy?.[f]} onChange={setPrivacy} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="sm:col-span-2 flex justify-end">
          <button onClick={save} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="profile-save">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <BadgeCheck size={16} />} Save profile
          </button>
        </div>
      </div>
    </div>
  );
}
